import React, { useEffect, useState } from "react";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import client, { API, getToken } from "@/lib/api";
import { toast } from "sonner";
import {
  Clock, Download, Trash2, UserPlus, ArrowLeftRight, TrendingUp, Palmtree, Undo2,
  Pencil, Paperclip, Archive, UserCog, Building2, User, FileText, Plus,
} from "lucide-react";

const EV_META = {
  hired: { icon: UserPlus, cls: "bg-green-100 text-green-700", key: "em_ev_hired" },
  created: { icon: UserPlus, cls: "bg-green-100 text-green-700", key: "em_ev_hired" },
  transfer: { icon: ArrowLeftRight, cls: "bg-sky-100 text-sky-700", key: "em_type_transfer" },
  supervisor_transfer: { icon: UserCog, cls: "bg-indigo-100 text-indigo-700", key: "em_type_sup_transfer" },
  branch_change: { icon: Building2, cls: "bg-sky-100 text-sky-700", key: "em_ev_branch_change" },
  supervisor_change: { icon: User, cls: "bg-indigo-100 text-indigo-700", key: "em_ev_supervisor_change" },
  am_change: { icon: UserCog, cls: "bg-violet-100 text-violet-700", key: "em_ev_am_change" },
  promotion: { icon: TrendingUp, cls: "bg-violet-100 text-violet-700", key: "em_type_promotion" },
  vacation: { icon: Palmtree, cls: "bg-emerald-100 text-emerald-700", key: "em_type_vacation" },
  vacation_return: { icon: Undo2, cls: "bg-teal-100 text-teal-700", key: "em_type_vacation_return" },
  data_edit: { icon: Pencil, cls: "bg-amber-100 text-amber-700", key: "em_ev_data_edit" },
  document_added: { icon: Paperclip, cls: "bg-slate-100 text-slate-600", key: "em_ev_document_added" },
  archived: { icon: Archive, cls: "bg-rose-100 text-rose-700", key: "em_ev_archived" },
};

const fmtAt = (at) => (at || "").slice(0, 16).replace("T", " ");

export function EMTimeline({ empId }) {
  const { t } = useT();
  const [events, setEvents] = useState(null);
  useEffect(() => {
    client.get(`/em/employees/${empId}/events`).then((r) => setEvents(r.data || [])).catch(() => setEvents([]));
  }, [empId]);
  if (!events) return <div className="text-xs text-slate-400 p-4">{t("loading")}</div>;
  const movements = events.filter((e) => e.kind === "movement");
  const edits = events.filter((e) => e.kind === "edit");
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-sm font-black text-slate-700 mb-2 flex items-center gap-1.5">
          <Clock size={15} className="text-sky-600" /> {t("em_timeline")} ({movements.length})
        </h2>
        {movements.length === 0 ? (
          <div className="bg-white rounded-2xl card-shadow p-6 text-center text-slate-400 text-sm" data-testid="em-timeline-empty">{t("em_no_events")}</div>
        ) : (
          <div className="bg-white rounded-2xl card-shadow p-4 relative" data-testid="em-timeline">
            <div className="absolute top-4 bottom-4 start-[27px] w-px bg-slate-200" />
            <div className="space-y-3">
              {movements.map((ev) => {
                const m = EV_META[ev.type] || EV_META.data_edit;
                const Icon = m.icon;
                return (
                  <div key={ev.id} className="flex items-start gap-3 relative" data-testid={`em-timeline-ev-${ev.id}`}>
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 z-10 ${m.cls}`}><Icon size={15} /></div>
                    <div className="min-w-0 flex-1 pb-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-black text-slate-800">{t(m.key)}</span>
                        <span className="text-[10px] text-slate-400" style={{ direction: "ltr" }}>{fmtAt(ev.at)}</span>
                      </div>
                      {ev.summary && <div className="text-[11px] text-slate-600 mt-0.5">{ev.summary}</div>}
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        {ev.actor_name && <>{t("em_by")}: <b>{ev.actor_name}</b></>}
                        {ev.approved_by && <> · {t("em_decided_by")}: <b>{ev.approved_by}</b></>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div>
        <h2 className="text-sm font-black text-slate-700 mb-2 flex items-center gap-1.5">
          <Pencil size={14} className="text-amber-600" /> {t("em_activity_log")} ({edits.length})
        </h2>
        {edits.length === 0 ? (
          <div className="bg-white rounded-2xl card-shadow p-6 text-center text-slate-400 text-sm" data-testid="em-activity-empty">{t("em_no_events")}</div>
        ) : (
          <div className="bg-white rounded-2xl card-shadow overflow-x-auto" data-testid="em-activity-log">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-500 border-b border-slate-100 bg-slate-50">
                  <th className="p-2.5 text-start font-bold">{t("em_by")}</th>
                  <th className="p-2.5 text-start font-bold">{t("date")}</th>
                  <th className="p-2.5 text-start font-bold">{t("em_field_col")}</th>
                  <th className="p-2.5 text-start font-bold">{t("em_old_val")}</th>
                  <th className="p-2.5 text-start font-bold">{t("em_new_val")}</th>
                </tr>
              </thead>
              <tbody>
                {edits.flatMap((ev) => (ev.changes?.length ? ev.changes : [{}]).map((c, i) => (
                  <tr key={`${ev.id}-${i}`} className="border-b border-slate-50" data-testid={`em-activity-row-${ev.id}-${i}`}>
                    <td className="p-2.5 font-bold text-slate-700 whitespace-nowrap">{i === 0 ? ev.actor_name || "—" : ""}</td>
                    <td className="p-2.5 text-slate-400 whitespace-nowrap" style={{ direction: "ltr" }}>{i === 0 ? fmtAt(ev.at) : ""}</td>
                    <td className="p-2.5 font-semibold text-slate-600">{c.label || c.field || "—"}</td>
                    <td className="p-2.5 text-rose-600">{String(c.old ?? "—")}</td>
                    <td className="p-2.5 text-green-700 font-bold">{String(c.new ?? "—")}</td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export function EMDocsPanel({ empId }) {
  const { t } = useT();
  const { user } = useAuth();
  const [cats, setCats] = useState([]);
  const [data, setData] = useState(null); // {documents, municipal_attachments}
  const [filter, setFilter] = useState("");
  const [form, setForm] = useState({ category_id: "", title: "", note: "", expiry_date: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newCat, setNewCat] = useState("");
  const isSuper = user?.role === "super_admin";

  const load = async () => {
    const [c, d] = await Promise.all([
      client.get("/em/doc-categories"),
      client.get(`/em/employees/${empId}/documents`),
    ]);
    setCats(c.data || []);
    setData(d.data || { documents: [], municipal_attachments: [] });
  };
  useEffect(() => { load().catch(() => setData({ documents: [], municipal_attachments: [] })); }, [empId]);

  const upload = async () => {
    if (!file || !form.category_id) { toast.error(t("em_doc_pick_required")); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category_id", form.category_id);
      fd.append("title", form.title);
      fd.append("note", form.note);
      fd.append("expiry_date", form.expiry_date);
      await client.post(`/em/employees/${empId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("em_doc_uploaded"));
      setForm({ category_id: "", title: "", note: "", expiry_date: "" });
      setFile(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("em_request_error")); }
    finally { setBusy(false); }
  };

  const del = async (doc) => {
    if (!window.confirm(t("em_doc_delete_confirm"))) return;
    try {
      await client.delete(`/em/documents/${doc.id}`);
      toast.success(t("em_doc_deleted"));
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("em_request_error")); }
  };

  const openDoc = async (doc) => {
    try {
      const res = await fetch(`${API}/em/documents/${doc.id}/file`, { headers: { Authorization: `Bearer ${getToken()}` } });
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error(t("em_download_error")); }
  };

  const openMunicipal = async (att) => {
    try {
      const res = await fetch(`${API}/employees/${empId}/attachments/${att.id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error(t("em_download_error")); }
  };

  const addCat = async () => {
    if (!newCat.trim()) return;
    try {
      await client.post("/em/doc-categories", { name: newCat.trim() });
      setNewCat("");
      const c = await client.get("/em/doc-categories");
      setCats(c.data || []);
      toast.success("✅");
    } catch (e) { toast.error(e?.response?.data?.detail || t("em_request_error")); }
  };

  if (!data) return <div className="text-xs text-slate-400 p-4">{t("loading")}</div>;
  const docs = filter ? data.documents.filter((d) => d.category_id === filter) : data.documents;

  return (
    <div className="space-y-4" data-testid="em-docs-panel">
      {/* Upload form */}
      <div className="bg-slate-50 rounded-xl p-3 space-y-2">
        <div className="text-xs font-black text-slate-700">{t("em_doc_upload")}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <select value={form.category_id} onChange={(e) => setForm((s) => ({ ...s, category_id: e.target.value }))} className="border border-slate-200 rounded-xl px-3 py-2 text-xs bg-white" data-testid="em-doc-category-select">
            <option value="">{t("em_doc_category")}…</option>
            {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input value={form.title} onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))} placeholder={t("em_doc_title_ph")} className="border border-slate-200 rounded-xl px-3 py-2 text-xs" data-testid="em-doc-title-input" />
          <input type="date" value={form.expiry_date} onChange={(e) => setForm((s) => ({ ...s, expiry_date: e.target.value }))} title={t("em_doc_expiry")} className="border border-slate-200 rounded-xl px-3 py-2 text-xs bg-white" data-testid="em-doc-expiry-input" />
          <input value={form.note} onChange={(e) => setForm((s) => ({ ...s, note: e.target.value }))} placeholder={t("em_doc_note_ph")} className="border border-slate-200 rounded-xl px-3 py-2 text-xs" data-testid="em-doc-note-input" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-xs" data-testid="em-doc-file-input" />
          <button onClick={upload} disabled={busy || !file || !form.category_id} className="inline-flex items-center gap-1 bg-sky-600 text-white text-xs font-bold rounded-xl px-3.5 py-2 hover:bg-sky-700 disabled:opacity-50" data-testid="em-doc-upload-btn">
            <Plus size={13} /> {t("em_doc_upload")}
          </button>
        </div>
        {isSuper && (
          <div className="flex items-center gap-2 pt-1 border-t border-slate-200">
            <input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder={t("em_doc_new_cat_ph")} className="border border-slate-200 rounded-xl px-3 py-1.5 text-[11px] w-44" data-testid="em-doc-new-cat-input" />
            <button onClick={addCat} disabled={!newCat.trim()} className="text-[11px] font-bold text-sky-600 hover:underline disabled:opacity-40" data-testid="em-doc-new-cat-btn">+ {t("em_doc_add_cat")}</button>
          </div>
        )}
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-1.5">
        <button onClick={() => setFilter("")} className={`px-3 py-1 rounded-full text-[11px] font-bold ${!filter ? "bg-sky-600 text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid="em-doc-filter-all">
          {t("all")} ({data.documents.length})
        </button>
        {cats.map((c) => {
          const n = data.documents.filter((d) => d.category_id === c.id).length;
          if (!n) return null;
          return (
            <button key={c.id} onClick={() => setFilter(c.id)} className={`px-3 py-1 rounded-full text-[11px] font-bold ${filter === c.id ? "bg-sky-600 text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid={`em-doc-filter-${c.id}`}>
              {c.name} ({n})
            </button>
          );
        })}
      </div>

      {/* Documents list */}
      {docs.length === 0 ? (
        <div className="text-center text-slate-400 text-xs py-4" data-testid="em-docs-empty">{t("em_no_docs")}</div>
      ) : (
        <div className="space-y-2">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-2.5 bg-white border border-slate-100 rounded-xl p-2.5" data-testid={`em-doc-${d.id}`}>
              <div className="w-8 h-8 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center shrink-0"><FileText size={15} /></div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-bold text-slate-800 truncate">{d.title}</div>
                <div className="text-[10px] text-slate-400">
                  {d.category_name} · {d.uploaded_by_name} · <span style={{ direction: "ltr" }}>{fmtAt(d.uploaded_at)}</span>
                  {d.expiry_date && <span className="text-amber-600 font-bold"> · {t("em_doc_expiry_short")}: {d.expiry_date}</span>}
                </div>
                {d.note && <div className="text-[10px] text-slate-500 truncate">{d.note}</div>}
              </div>
              <button onClick={() => openDoc(d)} className="p-1.5 rounded-lg text-sky-600 hover:bg-sky-50" title={t("download")} data-testid={`em-doc-open-${d.id}`}><Download size={14} /></button>
              <button onClick={() => del(d)} className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50" data-testid={`em-doc-del-${d.id}`}><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}

      {/* Municipality attachments (read-only) */}
      {data.municipal_attachments.length > 0 && (
        <div>
          <div className="text-[11px] font-black text-slate-500 mb-1.5">{t("em_municipal_attachments")} ({data.municipal_attachments.length})</div>
          <div className="space-y-1.5">
            {data.municipal_attachments.map((a) => (
              <button key={a.id} onClick={() => openMunicipal(a)} className="w-full flex items-center gap-2 bg-slate-50 rounded-xl p-2 text-start hover:bg-slate-100" data-testid={`em-doc-municipal-${a.id}`}>
                <Paperclip size={13} className="text-slate-400 shrink-0" />
                <span className="text-[11px] font-semibold text-slate-600 truncate flex-1">{a.filename || a.title || "attachment"}</span>
                <Download size={12} className="text-slate-400" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
